import React, { useEffect, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { useDispatch } from "react-redux";
import {
  getNotificationList,
  getReadAllNotification,
} from "../../redux/slices/notificationSlice";
import { useNavigate } from "react-router-dom";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import { ReactComponent as Clock } from "../../assets/images/time.svg";
import { getViewProfile } from "../../redux/slices/profileSlice";

const Notification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [notificationData, setNotification] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Track Activity
  usePageTracking("NOTIFICATIONS");
  useEffect(() => {
    dispatch(getViewProfile());
  }, []);
  useEffect(() => {
    const payload = {
      page_name: "NOTIFICATIONS",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "NOTIFICATIONS",
        }),
      );
    };
  }, [dispatch]);

  const _readNotification = async () => {
    try {
      dispatch(getReadAllNotification());
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    _readNotification();
  }, []);

  const _fetchNotification = async (page) => {
    if (loading) return;
    setLoading(true);

    try {
      let obj = {
        page: page,
        length: 10,
      };
      const result = await dispatch(getNotificationList(obj));
      const newData = result?.payload?.data?.data ?? [];
      const success = result?.payload?.success;

      if (success) {
        if (page === 1) {
          setNotification(newData);
        } else {
          setNotification((prev) => [...prev, ...newData]);
        }

        if (newData.length < 10) {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      if (page === 1) setInitialLoading(false); // ⬅ IMPORTANT
    }
  };
  useEffect(() => {
    _fetchNotification(page);
  }, [page]);

  useEffect(() => {
    _readNotification();
  }, []);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "100px", threshold: 0.1 },
    );

    const current = observerRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, [hasMore, loading]);

  // convert data
  const groupByDate = (notifications) => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    const getTodayOrYesterday = (dateString) => {
      const today = new Date();
      const date = new Date(dateString);
      // Reset time to midnight for accurate day comparison
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      const timeDiff = today.getTime() - date.getTime();
      const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      return formatDate(dateString);
    };

    return notifications.reduce((acc, notification) => {
      const dateLabel = getTodayOrYesterday(notification.created_at);
      let userInfo = null;
      try {
        const parsed = notification?.data ? JSON.parse(notification.data) : {};
        userInfo = parsed?.message?.data ?? null;
      } catch (e) {
        userInfo = null;
      }
      const notificationWithUserInfo = {
        ...notification,
        userInfo,
      };

      if (!acc[dateLabel]) {
        acc[dateLabel] = [];
      }
      acc[dateLabel].push(notificationWithUserInfo);

      return acc;
    }, {});
  };

  const flattenNotifications = (groupedNotifications) => {
    return Object.keys(groupedNotifications).map((date) => ({
      date,
      notifications: groupedNotifications[date].map((notification) => {
        let parsedData = {};
        try {
          parsedData = notification?.data ? JSON.parse(notification.data) : {};
        } catch (e) {
          parsedData = {};
        }

        const message = parsedData?.message?.notification ?? {};
        const extraData = parsedData?.message?.data ?? {};
        return {
          id: notification.id,
          type: notification.type,
          title: message.title ?? "",
          relation_id: notification.relation_id,
          author_id: notification.author_id,
          post_content: extraData.post_content ?? "",
          image: message.image || extraData.post_image_url || null,
          body: message.body ?? "",
          userInfo: notification?.userInfo ?? null,
          created_at: notification.created_at, // Add created_at timestamp
        };
      }),
    }));
  };

  const [groupedNotifications, setGroupedNotifications] = useState(
    groupByDate(notificationData),
  );
  const [flattenedNotifications, setFlattenedNotifications] = useState(
    flattenNotifications(groupedNotifications),
  );

  useEffect(() => {
    setGroupedNotifications(groupByDate(notificationData));
  }, [notificationData]);

  useEffect(() => {
    setFlattenedNotifications(flattenNotifications(groupedNotifications));
  }, [groupedNotifications]);
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const truncateText = (text, limit = 90) => {
    if (!text) return "";
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(text);
    const plain = hasHtml ? stripHtml(text) : text;
    return plain.length > limit ? plain.slice(0, limit) + "..." : plain;
  };

  // Format time like "11/24/2018 at 9:30 AM"
  const formatNotificationTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Get month, day, year
    const month = date.getMonth() + 1; // Months are 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();

    // Get hours and minutes
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${month}/${day}/${year} at ${hours}:${minutes} ${ampm}`;
  };

  return (
    <div className="notification-content">
      <NoIndexSEO title={seoConfig.notification.title} />
      <div className="app-page-title">
        <div className="page-title-heading">
          <h3 className="page-title">Notification</h3>
        </div>
      </div>
      <Card className="pb-0 mb-0">
        <Card.Body>
          {initialLoading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div className="spinner-border text-primary"></div>
            </div>
          )}

          {/* NO NOTIFICATIONS AT ALL */}
          {!initialLoading && notificationData.length === 0 && (
            <p className="p-4 text-center">No notifications found.</p>
          )}

          {flattenedNotifications.map((group, groupIndex) => (
            <div className="notification-box-content" key={groupIndex}>
              <div className="notification-title">{group.date}</div>
              {group.notifications.map((item, index) => {
                // let profileUrl = null;

                // if (item?.userInfo?.sender_profile_url) {
                //   profileUrl = `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${item.userInfo.sender_profile_url}`;
                // }

                // else if (item?.userInfo?.post_image_url) {
                //   profileUrl = `${process.env.REACT_APP_API_BASE_URL}/${item.userInfo.post_image_url}`;
                // }
                let profileUrl = null;

                // 1. sender profile image
                if (item?.userInfo?.sender_profile_url) {
                  profileUrl = `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${item.userInfo.sender_profile_url}`;
                }

                // 2. notification image (post/job)
                else if (item?.image) {
                  profileUrl = `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${item.image}`;
                }
                // const navigateToPostDetails = () => {
                //   if (item?.relation_id === 0 || item?.author_id === 0) return; // ❌ do nothing

                //   navigate(`/notification/${item?.userInfo?.post_id}`, {
                //     state: {
                //       comment_id: parseInt(item?.userInfo?.comment_id),
                //       userinfo: item?.userInfo,
                //     },
                //   });
                // };
                const navigateToPostDetails = () => {
                  const type = item?.userInfo?.type;

                  // COMMUNITY — use nested post_id first, fall back to top-level relation_id
                  if (
                    type === "community_update" ||
                    type === "new_post" ||
                    type === "new_comment" ||
                    type === "post_like" ||
                    type === "new_reply" ||
                    type === "poll_vote" ||
                    type === "reply_on_post" ||
                    type === "comment_mention"
                  ) {
                    const postId = item?.userInfo?.post_id || item?.relation_id;
                    if (!postId) {
                      navigate("/community");
                      return;
                    }

                    navigate(`/notification/${postId}`, {
                      state: {
                        comment_id: parseInt(item?.userInfo?.comment_id),
                        userinfo: item?.userInfo,
                      },
                    });
                  }

                  // CHAT MESSAGE
                  else if (type === "chat_message") {
                    const senderId = item?.userInfo?.sender_id;
                    if (senderId) {
                      navigate(`/messaging/${senderId}`);
                    }
                  }

                  // JOB NOTIFICATIONS ✅
                  else if (
                    type === "new_job_posted"
                    // type === "application_qualified" ||
                    // type === "application_hired"
                  ) {
                    // If job_id exists, navigate to job detail page
                    if (item?.userInfo?.job_id) {
                      navigate(`/job-detail/${item?.userInfo?.job_id}`);
                    } else {
                      // If no job_id, redirect to jobs page
                      navigate("/jobs");
                    }
                  } else if (
                    type === "application_qualified" ||
                    type === "application_hired" ||
                    type === "application_offer" ||
                    type === "application_interviewed" ||
                    type === "interview_scheduled"
                  ) {
                    navigate("/jobs", {
                      state: {
                        tab: "applied",
                      },
                    });
                  }

                  // RE-ENGAGEMENT
                  else if (type === "re_engagement_reminder") {
                    navigate("/dashboard");
                  } else if (type === "user_followed") {
                    navigate("/profile");
                  }
                };
                const isClickable =
                  item?.userInfo?.type !== "re_engagement_reminder";
                let imageData = String;

                const { type, post_image_url, sender_profile_url } =
                  item?.userInfo || {};
                if (type === "new_post" && post_image_url) {
                  imageData = `${String}/${post_image_url}`;
                } else if (sender_profile_url) {
                  imageData = `${String}/${sender_profile_url}`;
                }

                return (
                  // <div
                  //   className="notification-box"
                  //   key={index}
                  //   onClick={() => navigateToPostDetails()}
                  // >
                  <div
                    className="notification-box"
                    key={index}
                    onClick={() => navigateToPostDetails()}
                    // style={{
                    //   cursor:
                    //     item?.relation_id === 0 || item?.author_id === 0
                    //       ? "default"
                    //       : "pointer",
                    //   opacity:
                    //     item?.relation_id === 0 || item?.author_id === 0
                    //       ? 0.8
                    //       : 1,
                    // }}
                    style={{
                      cursor: isClickable ? "pointer" : "default",
                      opacity: isClickable ? 1 : 0.8,
                    }}
                  >
                    <div className="notification">
                      <div className="icon">
                        <img
                          // src={
                          //   profileUrl
                          //     ? profileUrl
                          //     : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          //       item.userInfo?.sender_name
                          //     )}&background=0195D3&color=fff&size=24`
                          // }
                          src={
                            profileUrl
                              ? profileUrl
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                  item.userInfo?.sender_name || "Admin",
                                )}&background=0195D3&color=fff&size=24`
                          }
                          alt="profile"
                          onError={(e) => {
                            e.target.onerror = null; // prevent infinite loop
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              item.userInfo?.sender_name || "Admin",
                            )}&background=0195D3&color=fff&size=24`;
                          }}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div className="content">
                        <div className="title">{item.title}</div>
                        {/* <div className="description">
                          {item?.userInfo?.type === "community_update"
                            ? item?.userInfo?.post_content
                            : item.body}
                        </div> */}
                        <div className="description">
                          {truncateText(
                            item.body || item?.userInfo?.post_content,
                            90,
                          )}
                        </div>
                        <div className="notification-time">
                          <Clock className="clock-icon" />
                          {formatNotificationTime(item.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </Card.Body>
      </Card>
      {!initialLoading && notificationData.length > 0 && hasMore && (
        <div
          ref={observerRef}
          style={{
            height: "60px",
            margin: "20px 0",
            textAlign: "center",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;
